import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { PreviewCell } from '../../utils/export-preview-model';
import { diffValues, hasAnchor } from '../../utils/export-value-diff';

interface PreviewChangesViewProps {
  /** Cells for the product on screen — already narrowed to the changed ones. */
  cells: PreviewCell[];
  /** See `PreviewFileView`'s prop of the same name: every existing caller renders this inside a
   *  `Sheet` that owns the page's one scroller, so the default stays exactly as it was. The
   *  catalog builder's "File preview" tab opts in instead of forking this view. */
  boundedHeight?: boolean;
}

/**
 * What this template did to THIS product, as a change log.
 *
 * Replaces a stack of full-width cards, which had two problems. Each card
 * carried the encoding's whole description as body prose — text written for
 * whoever maintains the encodings ("the stored jsonb has FOUR shapes on dev…"),
 * four lines of it, above the two values it was meant to explain. And five
 * changes made five tall cards, so the panel grew far past the drawer and
 * pushed its own footer out of reach.
 *
 * One row per changed field instead: what changed, which rule changed it, and
 * the change itself. Five changes are five rows, not five screens. The rule's
 * description moves behind an info affordance, where it is available without
 * being in the way — a reader who wants "what happened" is not made to read
 * the implementation notes first.
 *
 * The value cell shows the diff anchored in place when the two values share a
 * start or an end, which is the common case: an encoding usually trims or
 * rewraps rather than rewriting. With no shared anchor there is nothing to
 * align on, so it falls back to `stored → exported`. Rows are clamped to two
 * lines and expand on click, which is what keeps a JSON product detail from
 * setting the height of the whole panel.
 *
 * Removal and addition are carried by a glyph and a word as well as a tint —
 * colour alone is not an accessible signal.
 */
export function PreviewChangesView({ cells, boundedHeight = false }: PreviewChangesViewProps) {
  const { t } = useTranslation('exports');
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    // Default: no scroller of its own — the table is `w-full` and the drawer's region owns the
    // vertical axis (see `preview-file-view`). `boundedHeight` opts into its own bounded box for a
    // caller with no such outer scroller.
    <div
      data-testid="export-preview-changes"
      className={cn(
        'rounded-md border',
        boundedHeight && 'max-h-[70vh] min-h-0 flex-1 overflow-y-auto',
      )}
    >
      <table
        data-testid="export-preview-changes-table"
        className="w-full border-separate border-spacing-0 text-xs"
      >
        <caption className="sr-only">{t('preview.changes.caption')}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="bg-selected-row text-muted-foreground sticky top-0 z-10 w-[30%] border-b px-3 py-2 text-left text-xs font-semibold"
            >
              {t('preview.changes.field')}
            </th>
            <th
              scope="col"
              className="bg-selected-row text-muted-foreground sticky top-0 z-10 w-[1%] border-b px-3 py-2 text-left text-xs font-semibold whitespace-nowrap"
            >
              {t('preview.changes.rule')}
            </th>
            <th
              scope="col"
              className="bg-selected-row text-muted-foreground sticky top-0 z-10 border-b px-3 py-2 text-left text-xs font-semibold"
            >
              {t('preview.changes.change')}
            </th>
          </tr>
        </thead>
        <tbody>
          {cells.map((cell) => {
            const diff = diffValues(cell.raw, cell.encoded);
            const anchored = hasAnchor(diff);
            const isOpen = expanded.has(cell.key);

            return (
              <tr
                key={cell.key}
                data-testid={`export-preview-changes-row-${cell.key}`}
                className="hover:bg-selected-row/40 group cursor-pointer align-top"
                onClick={() => {
                  toggle(cell.key);
                }}
              >
                <td className="border-b px-3 py-2">
                  <span className="flex items-start gap-1">
                    {isOpen ? (
                      <ChevronDown
                        aria-hidden="true"
                        className="text-muted-foreground mt-0.5 size-3 shrink-0"
                      />
                    ) : (
                      <ChevronRight
                        aria-hidden="true"
                        className="text-muted-foreground mt-0.5 size-3 shrink-0"
                      />
                    )}
                    <span className="min-w-0">
                      <span className="text-foreground block font-medium">{cell.label}</span>
                      <span className="text-muted-foreground block font-mono text-[11px] break-all">
                        {cell.key}
                      </span>
                      {cell.identifier && (
                        <Badge
                          variant="outline"
                          className="pointer-events-none mt-0.5 text-[10px] font-normal"
                        >
                          {t('preview.changes.identifier')}
                        </Badge>
                      )}
                    </span>
                  </span>
                </td>

                <td className="border-b px-3 py-2 whitespace-nowrap">
                  {cell.transformationLabel && (
                    <span className="flex items-center gap-1">
                      <Badge
                        variant="secondary"
                        className="pointer-events-none text-[11px] font-normal"
                      >
                        {cell.transformationLabel}
                      </Badge>
                      {/* The rule's own words, on demand. Inline they buried
                          the values they were explaining. */}
                      {cell.transformationDescription && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              data-testid={`export-preview-changes-rule-info-${cell.key}`}
                              className="text-muted-foreground hover:text-foreground cursor-help"
                              aria-label={t('preview.changes.ruleAria', {
                                rule: cell.transformationLabel,
                              })}
                              onClick={(event) => {
                                // Row click toggles the value; the tooltip is
                                // its own affordance and must not do both.
                                event.stopPropagation();
                              }}
                            >
                              <Info aria-hidden="true" className="size-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm text-xs leading-relaxed">
                            {cell.transformationDescription}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  )}
                </td>

                <td className="border-b px-3 py-2">
                  {anchored ? (
                    <span
                      className={cn(
                        'text-foreground block font-mono leading-relaxed break-all',
                        isOpen ? 'whitespace-pre-wrap' : 'line-clamp-2',
                      )}
                    >
                      {diff.prefix}
                      {diff.removed !== '' && (
                        <span className="bg-error-50 decoration-error rounded px-0.5 line-through">
                          {diff.removed}
                        </span>
                      )}
                      {diff.added !== '' && (
                        <span className="bg-success-50 rounded px-0.5">{diff.added}</span>
                      )}
                      {diff.suffix}
                    </span>
                  ) : (
                    <span className="block space-y-0.5">
                      <span
                        className={cn(
                          'text-muted-foreground block font-mono leading-relaxed break-all line-through',
                          isOpen ? 'whitespace-pre-wrap' : 'line-clamp-2',
                        )}
                      >
                        {cell.raw || t('preview.changes.emptyValue')}
                      </span>
                      <span
                        className={cn(
                          'text-foreground block font-mono leading-relaxed break-all',
                          isOpen ? 'whitespace-pre-wrap' : 'line-clamp-2',
                        )}
                      >
                        <ArrowRight
                          aria-hidden="true"
                          className="text-success mr-1 inline size-3"
                        />
                        {cell.encoded || t('preview.changes.emptyValue')}
                      </span>
                    </span>
                  )}

                  {/* Legend only where a tint is actually used, and only while
                      the row is open — repeated on five collapsed rows it is
                      noise, not help. */}
                  {isOpen && anchored && (
                    <span className="text-muted-foreground mt-1 block text-[11px]">
                      {diff.removed !== '' && t('preview.changes.removedLegend')}
                      {diff.removed !== '' && diff.added !== '' && ' · '}
                      {diff.added !== '' && t('preview.changes.addedLegend')}
                    </span>
                  )}

                  {cell.specWarning && (
                    <span
                      data-testid={`export-preview-changes-spec-warning-${cell.key}`}
                      className="text-warning mt-1 flex items-start gap-1 text-[11px]"
                    >
                      <AlertTriangle aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
                      {cell.specWarning.kind === 'length'
                        ? t('preview.spec.length', {
                            actual: cell.specWarning.actual,
                            limit: cell.specWarning.limit,
                          })
                        : t('preview.spec.repetitions', {
                            actual: cell.specWarning.actual,
                            limit: cell.specWarning.limit,
                          })}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
